import {
  apiPutUsersUuid,
  FeatureFlagState,
  FeatureFlagUpdate,
  UserFeatureFlag,
} from ".";

export type FeatureFlags = Partial<Record<UserFeatureFlag, FeatureFlagState>>;

/**
 * Set one feature-flag sub-field on one user, answering with the registry the
 * API merged - so callers patch their copy of the user in place rather than
 * refetching.
 *
 * Only the sub-field being changed is sent, never a whole `feature_flags`
 * object. The API merges per sent key over storage that is sparse on purpose,
 * so sending the merged registry back would freeze every unset flag at today's
 * default; and sending `user_editable` alongside a value would demand staff
 * permissions a caller with the medical-update carve-out may not have. That
 * carve-out also needs the payload to carry nothing but flags - any other
 * changed field turns it into a 403.
 */
export async function putUserFeatureFlag(
  uuid: string,
  flag: UserFeatureFlag,
  update: FeatureFlagUpdate,
): Promise<FeatureFlags> {
  const { data } = await apiPutUsersUuid({
    path: { uuid },
    body: { feature_flags: { [flag]: update } },
  });
  return data.feature_flags ?? {};
}

/**
 * Whether a user response carries a flag registry to render. Absent or empty
 * means an API that predates the registry - the merged view is never partial.
 */
export function hasFeatureFlags(flags: FeatureFlags | undefined): boolean {
  return Object.keys(flags ?? {}).length > 0;
}
